import { Constructor } from '@deltasierra/type-utilities';
import { mapValues } from '@deltasierra/utilities-object';

type PropertyPathAccessorMeta<T> = {
    __path: string;
    __value: T;
};

export type PropertyPathAccessor<T> = T extends object
    ? PropertyPathAccessorMeta<T> & { readonly [K in keyof T]: PropertyPathAccessor<T[K]> }
    : PropertyPathAccessorMeta<T>;

/**
 * Creates a new property path accessor (PPA) for the given type.
 * A PPA allows you to easily generate dot-notation paths for object access
 * in a type-safe manner.
 *
 * @example
 * type MyType = {
 *     person: {
 *         name: string;
 *     }
 * };
 * const myObject: MyType = { ... };
 * const ppa = createPropertyPathAccessor<MyType>(myObject);
 * console.log(ppa.person.name.__path) // Output: 'person.name'
 * @param obj - The object to create the PPA for
 * @returns A PPA for the given object
 */
export function createPropertyPathAccessor<T extends { [key: string]: any }>(obj: T): PropertyPathAccessor<T> {
    const globalOrWindow: { [key: string]: any } = typeof global !== 'undefined' ? global : window;
    const ProxyConstructor: Constructor<any> | undefined = globalOrWindow.Proxy;
    return ProxyConstructor ? createPromiseVersion(ProxyConstructor, obj, '') : createNonPromiseVersion(obj, '');
}

function createPromiseVersion<T extends { [key: string]: any }>(
    ProxyConstructor: Constructor<any>,
    obj: T,
    root: string,
): PropertyPathAccessor<T> {
    return new ProxyConstructor(obj, {
        get(_: any, key: number | string | symbol) {
            if (key === '__path') {
                return root;
            } else if (key === '__value') {
                return obj;
            } else {
                return handleValueAccess(key.toString(), obj, root, (nextObj, nextRoot) =>
                    createPromiseVersion(ProxyConstructor, nextObj, nextRoot),
                );
            }
        },
    });
}

function createNonPromiseVersion<T extends { [key: string]: any }>(obj: T, root: string): PropertyPathAccessor<T> {
    const mappedValues = mapValues(obj, (_, key) =>
        handleValueAccess(key.toString(), obj, root, createNonPromiseVersion),
    );
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {
        ...mappedValues,
        __path: root,
        __value: obj,
    } as PropertyPathAccessor<T>;
}

function handleValueAccess<T extends Record<string, any>, K extends string & keyof T>(
    key: K,
    obj: T,
    root: string,
    ppaCreator: (obj: T[K], root: string) => PropertyPathAccessor<T[K]>,
): PropertyPathAccessor<T[K]> {
    const isArrayAccess = !isNaN(parseInt(key.toString(), 10));
    const newPath = `${root}${isArrayAccess ? `[${key}]` : `${root ? '.' : ''}${key}`}`;
    const nextValue = obj[key];
    if (typeof nextValue === 'object' && nextValue) {
        return ppaCreator(nextValue, newPath);
    } else {
        return {
            __path: newPath,
            __value: nextValue,
        } as any;
    }
}
